import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sliders, Loader2, CheckCircle2, Smile, Hash, Laugh, Pen, Save,
  AlignLeft, Zap, Anchor, Heart, Megaphone, Flame, Users, Info, Lightbulb,
  Briefcase, GraduationCap, Feather, Share2, Layers, Globe, ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/api-fetch";

interface VoicePreferences {
  emojiStyle: "love" | "sparingly" | "never";
  hashtagUsage: "always" | "sometimes" | "never";
  humorLevel: "serious" | "playful" | "jokester";
  writingStyle: "professional" | "casual" | "raw";
  postLength: "short" | "medium" | "long";
  sentenceStyle: "staccato" | "varied" | "flowing";
  hookStyle: "bold-claim" | "personal-story" | "question" | "stat-data" | "hot-take";
  personalSharing: "very-personal" | "balanced" | "keep-it-pro";
  ctaStyle: "direct-ask" | "subtle-nudge" | "engage-question" | "no-cta";
  languageEdge: "clean" | "mild-edge" | "unfiltered";
  favoriteEmojis?: string;
  targetAudience?: string;
  additionalContext?: string;
  coachingEnabled?: boolean;
  useCaseContext?: string | null;
  subUseCaseContext?: string | null;
}

const defaultPrefs: VoicePreferences = {
  emojiStyle: "sparingly",
  hashtagUsage: "never",
  humorLevel: "playful",
  writingStyle: "casual",
  postLength: "medium",
  sentenceStyle: "varied",
  hookStyle: "bold-claim",
  personalSharing: "balanced",
  ctaStyle: "engage-question",
  languageEdge: "clean",
  favoriteEmojis: "",
  targetAudience: "",
  additionalContext: "",
};

type OptionDef<T extends string> = { value: T; label: string };

function OptionPills<T extends string>({
  options,
  value,
  onChange,
  hint,
}: {
  options: OptionDef<T>[];
  value: T;
  onChange: (v: T) => void;
  hint?: { value: T; label: string } | null;
}) {
  return (
    <div className="space-y-2">
      {hint && hint.value !== value && (
        <div className="flex items-center gap-1.5 text-[11px] text-primary/70">
          <Info className="w-3 h-3 shrink-0" />
          <span>AI detected from your samples: <strong>{hint.label}</strong></span>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-all border ${
              value === opt.value
                ? "bg-primary text-primary-foreground border-primary shadow-sm shadow-primary/20"
                : "bg-secondary/60 text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const SUB_PERSONAS_MAP: Record<string, Array<{ id: string; label: string; desc: string }>> = {
  work: [
    { id: "work-email",      label: "External Emails",    desc: "Client emails, outreach, proposals" },
    { id: "work-messaging",  label: "Internal Messaging", desc: "Slack, standups, team updates" },
    { id: "work-docs",       label: "Docs & Reports",     desc: "Performance reviews, SOPs, specs" },
    { id: "work-sales",      label: "Sales Writing",      desc: "Cold outreach, pitches, follow-ups" },
    { id: "work-leadership", label: "Leadership Comms",   desc: "All-hands, board updates, vision" },
  ],
  school: [
    { id: "school-essays",        label: "Academic Essays",   desc: "Thesis, lit reviews, research papers" },
    { id: "school-career",        label: "Career Writing",    desc: "Cover letters, LinkedIn, thank-yous" },
    { id: "school-notes",         label: "Study & Notes",     desc: "Summaries, flashcards, concept maps" },
    { id: "school-presentations", label: "Presentations",     desc: "Speech outlines, slide copy, pitches" },
  ],
  creative: [
    { id: "creative-content",    label: "Content Creator",    desc: "Instagram, TikTok, YouTube, carousels" },
    { id: "creative-newsletter", label: "Newsletter Writer",  desc: "Substack, email intros, subject lines" },
    { id: "creative-blogger",    label: "Long-form / Blog",   desc: "Essays, opinion columns, blog posts" },
    { id: "creative-fiction",    label: "Fiction & Stories",  desc: "Short stories, screenplays, characters" },
    { id: "creative-artist",     label: "Artist & Portfolio", desc: "Artist statements, gallery copy, grants" },
  ],
  social: [
    { id: "social-linkedin",   label: "Personal Brand",     desc: "LinkedIn thought leadership, bio" },
    { id: "social-lifestyle",  label: "Lifestyle Content",  desc: "Instagram, travel, food, daily life" },
    { id: "social-community",  label: "Community & Forums", desc: "Discord, Reddit, niche community posts" },
    { id: "social-ecommerce",  label: "Products & Selling", desc: "Product descriptions, Etsy, seller profiles" },
  ],
  personal: [
    { id: "personal-relationships", label: "Relationships",    desc: "Messages to friends, family, partners" },
    { id: "personal-celebration",   label: "Celebrations",     desc: "Toasts, speeches, congratulations" },
    { id: "personal-difficult",     label: "Difficult Convos", desc: "Apologies, feedback, conflict resolution" },
    { id: "personal-journal",       label: "Journaling",       desc: "Daily logs, goal-setting, self-discovery" },
  ],
};

function Divider() {
  return <div className="border-t border-border" />;
}

function PreferenceRow({
  icon: Icon,
  label,
  sublabel,
  children,
}: {
  icon: React.ElementType;
  label: string;
  sublabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary shrink-0" />
          <p className="text-sm font-semibold text-foreground">{label}</p>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 ml-6">{sublabel}</p>
      </div>
      <div className="ml-6">{children}</div>
    </div>
  );
}

export function VoicePreferencesSection() {
  const { sessionId } = useAppStore();
  const { toast } = useToast();
  const [prefs, setPrefs] = useState<VoicePreferences>(defaultPrefs);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [voiceAnalysis, setVoiceAnalysis] = useState<Record<string, unknown> | null>(null);

  const [everOpened, setEverOpened] = useState(() => {
    try { return localStorage.getItem("voice_prefs_seen") === "1"; } catch { return false; }
  });
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next && !everOpened) {
      setEverOpened(true);
      try { localStorage.setItem("voice_prefs_seen", "1"); } catch { /* ignore */ }
    }
  };

  const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");

  useEffect(() => {
    async function load() {
      try {
        const [prefRes, profileRes] = await Promise.all([
          apiFetch(`${baseUrl}/api/voice/preferences`, { headers: { "x-session-id": sessionId } }),
          apiFetch(`${baseUrl}/api/voice/profile`, { headers: { "x-session-id": sessionId } }),
        ]);
        if (prefRes.ok) {
          const data = await prefRes.json();
          if (data.preferences) setPrefs((p) => ({ ...p, ...data.preferences }));
        }
        if (profileRes.ok) {
          const data = await profileRes.json();
          if (data.analysis) setVoiceAnalysis(data.analysis as Record<string, unknown>);
        }
      } catch {
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [sessionId, baseUrl]);

  const detected = {
    emojiStyle: voiceAnalysis
      ? (voiceAnalysis.emojiUsage as string) === "heavy"
        ? { value: "love" as const, label: "Love them (heavy usage)" }
        : (voiceAnalysis.emojiUsage as string) === "none" || (voiceAnalysis.emojiUsage as string) === "minimal"
        ? { value: "never" as const, label: "Never (none in samples)" }
        : { value: "sparingly" as const, label: "Sparingly" }
      : null,
    humorLevel: voiceAnalysis
      ? (voiceAnalysis.humorLevel as string) === "high"
        ? { value: "jokester" as const, label: "Full jokester (high humor)" }
        : (voiceAnalysis.humorLevel as string) === "low" || (voiceAnalysis.humorLevel as string) === "none"
        ? { value: "serious" as const, label: "Serious" }
        : { value: "playful" as const, label: "Bit playful" }
      : null,
    sentenceStyle: voiceAnalysis
      ? (voiceAnalysis.cadenceScore as number) < 35
        ? { value: "staccato" as const, label: "Short & staccato" }
        : (voiceAnalysis.cadenceScore as number) > 65
        ? { value: "flowing" as const, label: "Long & flowing" }
        : { value: "varied" as const, label: "Mixed/varied" }
      : null,
    hookStyle: voiceAnalysis && (voiceAnalysis.hookTaxonomy as string)
      ? { value: null, label: `"${String(voiceAnalysis.hookTaxonomy).slice(0, 60)}"` }
      : null,
    writingStyle: voiceAnalysis
      ? (voiceAnalysis.vocabularyComplexity as string) === "high"
        ? { value: "professional" as const, label: "Professional (complex vocab)" }
        : (voiceAnalysis.tone as string) === "casual" || (voiceAnalysis.tone as string) === "conversational"
        ? { value: "casual" as const, label: "Casual" }
        : null
      : null,
  };

  const set = <K extends keyof VoicePreferences>(key: K, val: VoicePreferences[K]) =>
    setPrefs((p) => ({ ...p, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`${baseUrl}/api/voice/preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ preferences: prefs }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast({ title: (data as { error?: string }).error || "Failed to save preferences", variant: "destructive" });
        return;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      toast({ title: "Preferences saved!", description: "Every future post will reflect these settings." });
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-3" id="voice-preferences">
      {/* ── Collapsible header ── */}
      <div className="relative">
        {!everOpened && (
          <span className="absolute -inset-1 rounded-2xl bg-primary/8 animate-pulse pointer-events-none" />
        )}
        <button
          type="button"
          data-testid="voice-prefs-toggle"
          onClick={handleToggle}
          className={`relative w-full flex items-center gap-2 py-2 px-3 rounded-xl transition-all hover:bg-secondary/40 ${
            !everOpened ? "ring-2 ring-primary/35" : ""
          }`}
        >
          <Sliders className="w-5 h-5 text-primary shrink-0" />
          <h2 className="text-xl font-display font-bold flex-1 text-left">Voice Preferences</h2>
          {!everOpened && (
            <span className="text-[11px] text-primary/70 font-medium whitespace-nowrap hidden sm:inline">
              Customize your writing defaults
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 text-muted-foreground/60 transition-transform duration-250 shrink-0 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {/* ── Collapsible body ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="voice-prefs-body"
            data-testid="voice-prefs-body"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            {loading ? (
              <div className="bg-card border border-border rounded-2xl p-6 flex items-center justify-center min-h-[120px]">
                <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  These settings override the AI's defaults and layer on top of your trained voice to dial in exactly how you write.
                  {voiceAnalysis && (
                    <span className="ml-1 text-primary/80">Your Voice DNA is active — hints show what we detected from your samples.</span>
                  )}
                </p>

      <div className="bg-card border border-border rounded-2xl divide-y divide-border overflow-hidden">

        {/* ── Emojis ── */}
        <div className="p-5 space-y-3">
          <PreferenceRow icon={Smile} label="Emojis" sublabel="How often do you use emojis when writing?">
            <OptionPills
              options={[
                { value: "love" as const, label: "Love them — sprinkle freely" },
                { value: "sparingly" as const, label: "Sparingly — 1 or 2 max" },
                { value: "never" as const, label: "Never" },
              ]}
              value={prefs.emojiStyle}
              onChange={(v) => set("emojiStyle", v)}
              hint={detected.emojiStyle}
            />
            <AnimatePresence>
              {prefs.emojiStyle !== "never" && (
                <motion.div
                  key="emoji-favs"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3"
                >
                  <input
                    type="text"
                    value={prefs.favoriteEmojis ?? ""}
                    onChange={(e) => set("favoriteEmojis", e.target.value)}
                    placeholder="Favorite emojis to use (e.g. 🔥 💡 ✅ — optional)"
                    className="w-full h-10 px-3 rounded-xl bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </PreferenceRow>
        </div>

        {/* ── Hashtags ── */}
        <div className="p-5">
          <PreferenceRow icon={Hash} label="Hashtags" sublabel="Do you use hashtags at the end of posts?">
            <OptionPills
              options={[
                { value: "always" as const, label: "Always — 2–5 hashtags" },
                { value: "sometimes" as const, label: "Sometimes — 1–2 if they fit" },
                { value: "never" as const, label: "Never" },
              ]}
              value={prefs.hashtagUsage}
              onChange={(v) => set("hashtagUsage", v)}
              hint={null}
            />
          </PreferenceRow>
        </div>

        {/* ── Humor ── */}
        <div className="p-5">
          <PreferenceRow icon={Laugh} label="Humor" sublabel="What's your humor dial set to?">
            <OptionPills
              options={[
                { value: "serious" as const, label: "Serious — no jokes" },
                { value: "playful" as const, label: "Bit playful" },
                { value: "jokester" as const, label: "Full jokester" },
              ]}
              value={prefs.humorLevel}
              onChange={(v) => set("humorLevel", v)}
              hint={detected.humorLevel}
            />
          </PreferenceRow>
        </div>

        {/* ── Writing style ── */}
        <div className="p-5">
          <PreferenceRow icon={Pen} label="Writing Style" sublabel="How formal is your online voice?">
            <OptionPills
              options={[
                { value: "professional" as const, label: "Professional" },
                { value: "casual" as const, label: "Casual" },
                { value: "raw" as const, label: "Raw & Unfiltered" },
              ]}
              value={prefs.writingStyle}
              onChange={(v) => set("writingStyle", v)}
              hint={detected.writingStyle ?? null}
            />
          </PreferenceRow>
        </div>

        {/* ── Language edge ── */}
        <div className="p-5">
          <PreferenceRow icon={Flame} label="Language Edge" sublabel="How edgy or clean should the language be?">
            <OptionPills
              options={[
                { value: "clean" as const, label: "Keep it clean" },
                { value: "mild-edge" as const, label: "Mild edge (damn, hell, etc.)" },
                { value: "unfiltered" as const, label: "Unfiltered — swear if it fits" },
              ]}
              value={prefs.languageEdge}
              onChange={(v) => set("languageEdge", v)}
              hint={null}
            />
          </PreferenceRow>
        </div>

        {/* ── Post length ── */}
        <div className="p-5">
          <PreferenceRow icon={AlignLeft} label="Post Length" sublabel="How much real estate do you like to take up?">
            <OptionPills
              options={[
                { value: "short" as const, label: "Short & punchy" },
                { value: "medium" as const, label: "Medium — balanced" },
                { value: "long" as const, label: "Long-form storytelling" },
              ]}
              value={prefs.postLength}
              onChange={(v) => set("postLength", v)}
              hint={null}
            />
          </PreferenceRow>
        </div>

        {/* ── Sentence style ── */}
        <div className="p-5">
          <PreferenceRow icon={Zap} label="Sentence Style" sublabel="What's your sentence rhythm like?">
            <OptionPills
              options={[
                { value: "staccato" as const, label: "Short. Punchy. One idea per sentence." },
                { value: "varied" as const, label: "Mixed — short bursts with longer flows" },
                { value: "flowing" as const, label: "Long, flowing, connected thoughts" },
              ]}
              value={prefs.sentenceStyle}
              onChange={(v) => set("sentenceStyle", v)}
              hint={detected.sentenceStyle}
            />
          </PreferenceRow>
        </div>

        {/* ── Hook / opening style ── */}
        <div className="p-5">
          <PreferenceRow icon={Anchor} label="Opening Hook Style" sublabel="How do you like to open a post?">
            {detected.hookStyle && (
              <div className="flex items-start gap-1.5 text-[11px] text-primary/70 mb-2">
                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                <span>AI detected from your samples: {detected.hookStyle.label}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {(
                [
                  { value: "bold-claim" as const, label: "Bold claim" },
                  { value: "personal-story" as const, label: "Personal story" },
                  { value: "question" as const, label: "Provocative question" },
                  { value: "stat-data" as const, label: "Stat or data point" },
                  { value: "hot-take" as const, label: "Hot take / controversy" },
                ] as OptionDef<typeof prefs.hookStyle>[]
              ).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => set("hookStyle", opt.value)}
                  className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-all border ${
                    prefs.hookStyle === opt.value
                      ? "bg-primary text-primary-foreground border-primary shadow-sm shadow-primary/20"
                      : "bg-secondary/60 text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </PreferenceRow>
        </div>

        {/* ── Personal sharing ── */}
        <div className="p-5">
          <PreferenceRow icon={Heart} label="Personal Sharing" sublabel="How personal do you get in your content?">
            <OptionPills
              options={[
                { value: "very-personal" as const, label: "Very personal — go deep" },
                { value: "balanced" as const, label: "Balanced — relatable but anchored" },
                { value: "keep-it-pro" as const, label: "Keep it professional" },
              ]}
              value={prefs.personalSharing}
              onChange={(v) => set("personalSharing", v)}
              hint={null}
            />
          </PreferenceRow>
        </div>

        {/* ── CTA style ── */}
        <div className="p-5">
          <PreferenceRow icon={Megaphone} label="Call to Action" sublabel="How do you close out your posts?">
            <OptionPills
              options={[
                { value: "direct-ask" as const, label: "Direct ask — 'Follow me', 'Save this'" },
                { value: "subtle-nudge" as const, label: "Subtle nudge" },
                { value: "engage-question" as const, label: "End with a question" },
                { value: "no-cta" as const, label: "No CTA — let it stand alone" },
              ]}
              value={prefs.ctaStyle}
              onChange={(v) => set("ctaStyle", v)}
              hint={null}
            />
          </PreferenceRow>
        </div>

        {/* ── Target audience ── */}
        <div className="p-5">
          <PreferenceRow icon={Users} label="Your Audience" sublabel="Who are you writing for? This shapes how the AI pitches ideas.">
            <input
              type="text"
              value={prefs.targetAudience ?? ""}
              onChange={(e) => set("targetAudience", e.target.value)}
              placeholder="e.g. Startup founders, junior developers, fitness enthusiasts over 40, B2B SaaS marketers…"
              className="w-full h-10 px-3 rounded-xl bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </PreferenceRow>
        </div>

        {/* ── Additional context ── */}
        <div className="p-5">
          <PreferenceRow icon={Sliders} label="Anything Else?" sublabel="Niche, quirks, things to always avoid — the AI will take it seriously.">
            <textarea
              value={prefs.additionalContext ?? ""}
              onChange={(e) => set("additionalContext", e.target.value)}
              placeholder="e.g. I hate corporate buzzwords. I use em dashes. I never end with 'What do you think?' I reference stoicism sometimes. I swear in private but keep it PG online."
              rows={3}
              className="w-full px-3 py-2.5 rounded-xl bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
            />
          </PreferenceRow>
        </div>

        {/* ── Content Focus ── */}
        <div className="p-5">
          <PreferenceRow icon={Layers} label="Content Focus" sublabel="What do you mainly create? We'll surface the most relevant tools and templates.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { id: "none",     icon: Globe,         label: "General",      desc: "No specific focus" },
                { id: "work",     icon: Briefcase,     label: "Work",         desc: "Slack, emails, reports" },
                { id: "school",   icon: GraduationCap, label: "School",       desc: "Essays, notes, study" },
                { id: "creative", icon: Feather,       label: "Creative",     desc: "Stories, scripts, speeches" },
                { id: "social",   icon: Share2,        label: "Social Media", desc: "Posts, threads, captions" },
                { id: "personal", icon: Heart,         label: "Personal",     desc: "Journals, messages, letters" },
              ].map(({ id, icon: Icon, label, desc }) => {
                const isNone = id === "none";
                const selected = isNone ? !prefs.useCaseContext : prefs.useCaseContext === id;
                return (
                  <button
                    key={id}
                    type="button"
                    data-testid={`content-focus-${id}`}
                    onClick={() => {
                      const newUseCase = isNone ? null : id;
                      setPrefs((p) => ({
                        ...p,
                        useCaseContext: newUseCase,
                        subUseCaseContext: (p.useCaseContext !== newUseCase) ? null : p.subUseCaseContext,
                      }));
                    }}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-all ${
                      selected
                        ? "border-primary bg-primary/8"
                        : "border-border bg-secondary/30 hover:border-primary/40 hover:bg-secondary/60"
                    }`}
                  >
                    <Icon className={`w-4 h-4 shrink-0 ${selected ? "text-primary" : "text-muted-foreground"}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground leading-tight">{label}</p>
                      <p className="text-xs text-muted-foreground">{desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            <AnimatePresence>
              {prefs.useCaseContext && SUB_PERSONAS_MAP[prefs.useCaseContext] && (
                <motion.div
                  key={`sub-${prefs.useCaseContext}`}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.22, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="pt-3 space-y-2">
                    <p className="text-xs text-muted-foreground">
                      Narrow it down <span className="opacity-50">(optional)</span>
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {SUB_PERSONAS_MAP[prefs.useCaseContext].map((sp) => {
                        const isSelected = prefs.subUseCaseContext === sp.id;
                        return (
                          <button
                            key={sp.id}
                            type="button"
                            data-testid={`sub-focus-${sp.id}`}
                            onClick={() => set("subUseCaseContext", isSelected ? null : sp.id)}
                            className={`inline-flex flex-col items-start px-2.5 py-2 rounded-xl border text-left transition-all ${
                              isSelected
                                ? "border-primary bg-primary/8 shadow-sm"
                                : "border-border bg-secondary/20 hover:border-primary/30 hover:bg-secondary/50"
                            }`}
                          >
                            <span className="text-[11px] font-semibold text-foreground leading-tight">{sp.label}</span>
                            <span className="text-[10px] text-muted-foreground leading-tight">{sp.desc}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </PreferenceRow>
        </div>

        <Divider />

        {/* ── AI Post Coach ── */}
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Lightbulb className="w-4 h-4 text-amber-400 shrink-0" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">AI Post Coach</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                After each generation, the AI gives you one specific, actionable note to improve the post.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={prefs.coachingEnabled !== false}
              onClick={() => set("coachingEnabled", prefs.coachingEnabled === false ? true : false)}
              data-testid="coaching-toggle"
              className={`relative shrink-0 mt-0.5 inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                prefs.coachingEnabled !== false ? "bg-primary" : "bg-muted"
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                  prefs.coachingEnabled !== false ? "translate-x-[18px]" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        </div>

        {/* ── Save ── */}
        <div className="p-5 bg-secondary/20">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground max-w-xs">
              These preferences update immediately. Every post generated after saving will reflect them.
            </p>
            <Button onClick={handleSave} disabled={saving} className="rounded-xl shrink-0 ml-4">
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : saved ? (
                <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-400" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saved ? "Saved!" : "Save Preferences"}
            </Button>
          </div>
        </div>
      </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
